'use client';

import type { SubmitJoinApplicationServiceRequest } from '@musinsa/services-curator';
import { createContext, type ReactNode, useContext } from 'react';
import type { FunnelStep } from '../types';

interface FunnelContextValue {
  step: FunnelStep;
  formValues: SubmitJoinApplicationServiceRequest;
  updateFormValues: (values: Partial<SubmitJoinApplicationServiceRequest>) => void;
  onNextStep: () => void;
  onPrevStep: () => void;
  canProceedToNext: () => boolean;
  isDuplicated: boolean | null;
  duplicateMessage: string;
  duplicationResultsByIndex: Record<
    number,
    {
      isDuplicated: boolean | null;
      isChecking: boolean;
    }
  >;
  updateDuplicationResult: (
    index: number,
    result: {
      isDuplicated: boolean | null;
      isChecking: boolean;
    } | null,
  ) => void;
}

const FunnelContext = createContext<FunnelContextValue | null>(null);

interface Props {
  children: ReactNode;
  value: FunnelContextValue;
}

export const FunnelProvider = ({ children, value }: Props) => (
  <FunnelContext.Provider value={value}>{children}</FunnelContext.Provider>
);

export const useFunnelContext = () => {
  const context = useContext(FunnelContext);
  if (!context) {
    throw new Error('useFunnelContext must be used within a FunnelProvider');
  }
  return context;
};
